import { useState, useEffect, useMemo, useCallback } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { ConvertOIDValueToString } from '../../utils/configConverter';
import { getNode, getNodeWithStatus } from '../../api/nodeAPI';
import { getRegion, deleteRegion } from '../../api/regionAPI';
import { getRegionSaleReports } from '../../api/regionSaleReportAPI';
import {getDevices} from "../../api/deviceAPI";
import { renderOnline, renderOffline, renderAlarmm, renderWarningg, renderNormal } from '../../components/StatusChip';
import ConvertOIDValueToString2 from '../../utils/objectConverter';
import { exportRegionReport } from '../../utils/regionReportExporter';
import AddRegionDialog from './AddRegionDialog';
import EditRegionDialog from './EditRegionDialog';
import { useObjectData } from '../../hooks/useLocalStorageData';
import { useSnackbar } from '../../hooks/useSnackbar';

export const getRegionLevel = (region, allRegions) => {
  if (!region) return 0;
  if (region.parentRegion === null) {
    return 1;
  }
  const parentRegion = allRegions.find(r => r.idRegion === region.parentRegion);
  if (!parentRegion) {
    return 1;
  }
  return getRegionLevel(parentRegion, allRegions) + 1;
};

const getDetailColumns = (t, objectData) => {
  const alarmFields = ["20", "5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];
  const basicFields = ["2", "4", "6", "7", "18"];

  // Dinamik basic field kolonları oluştur
  const basicColumns = basicFields.map((fieldId) => {
    const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));
    const columnName = objectInfo?.name || `Field ${fieldId}`;

    return {
      field: `field_${fieldId}`,
      headerName: columnName,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
    };
  });

  // Dinamik alarm kolonları oluştur
  const alarmColumns = alarmFields.map((fieldId) => {
    const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));
    const columnName = objectInfo?.name || `Field ${fieldId}`;

    return {
      field: `field_${fieldId}`,
      headerName: columnName,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.row[`field_${fieldId}`];

        // "20" field'ı için özel durum - bağlantı durumu
        if (fieldId === "20") {
          // value 1 ise Online, 0 ise Offline
          if (value === "1" || value === 1) {
            return renderOnline("Online", t);
          } else if (value === "0" || value === 0) {
            return renderOffline("Offline", t);
          } else {
            return value || "-";
          }
        }

        // Diğer field'lar için alarm level'a göre chip göster
        const alarmLevel = params.row[`field_${fieldId}_alarmLevel`];

        if (alarmLevel === 2) {
          return renderAlarmm(value || "", t);
        } else if (alarmLevel === 1) {
          return renderWarningg(value || "", t);
        } else if (alarmLevel === 0) {
          return renderNormal(value || "", t);
        } else {
          return value || "-";
        }
      },
    };
  });

  return [
    {
      field: "nodeName",
      headerName: t("regions.dataGrid.nodeName"),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "terminal",
      headerName: t("regions.dataGrid.terminal"),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "field_71",
      headerName: objectData.find((obj) => obj.idObject === 71)?.name || "Field 71",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const value = params.row.field_71;
        return value && value !== "-" ? `${value} ₺` : value;
      },
    },
    {
      field: "field_72",
      headerName: objectData.find((obj) => obj.idObject === 72)?.name || "Field 72",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const value = params.row.field_72;
        return value && value !== "-" ? `${value} ₺` : value;
      },
    },
    {
      field: "alarm",
      headerName: t("regions.dataGrid.alarm"),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const alarmStatus = params.row.alarmStatus;
        if (alarmStatus === 2) {
          return renderAlarmm("", t);
        } else if (alarmStatus === 1) {
          return renderWarningg("", t);
        } else if (alarmStatus === 0) {
          return renderNormal("", t);
        } else {
          return "-";
        }
      },
    },
    ...alarmColumns,
    ...basicColumns,
  ];
};

const RegionGrid = ({ onRegionSelect }) => {
  const { t } = useTranslation();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('hierarchical');
  const [openDialog, setOpenDialog] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodesWithStatus, setNodesWithStatus] = useState([]);
  const [devices, setDevices] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [saleReportsResult, regionsResult, nodesResult] = await Promise.all([
          getRegionSaleReports(),
          getRegion(),
          getNode()
        ]);

        setRegions(regionsResult);

        // Satış verilerini map'e çevir
        const saleReportsMap = new Map();
        saleReportsResult.forEach(report => {
          const key = report.isSubRegion === 1
            ? `sub_${report.subRegionId}`
            : `main_${report.regionId}`;
          saleReportsMap.set(key, report);
        });

        // Tüm bölgeleri işle
        const allRegionsData = [];

        regionsResult.forEach(region => {
          // Ana bölge mi alt bölge mi kontrol et
          // Level hesapla: parentRegion yoksa Level 1, varsa parent'ın level'ı + 1
          const regionLevel = getRegionLevel(region, regionsResult);

          // Level 1'leri atla, sadece Level 2 ve Level 3'ü işle
          if (regionLevel === 1) {
            return;
          }

          // Level 2 = Ana bölge (isSubRegion: 0)
          // Level 3 = Alt bölge (isSubRegion: 1)
          const isSubRegion = regionLevel === 3 ? 1 : 0;

          const key = isSubRegion === 1
            ? `sub_${region.idRegion}`
            : `main_${region.idRegion}`;

          // Satış verisi var mı kontrol et
          const saleReport = saleReportsMap.get(key);

          if (saleReport) {
            // Satış verisi varsa mevcut veriyi kullan
            allRegionsData.push(saleReport);
          } else {
            // Satış verisi yoksa, temel veriyi ekle
            const parentRegion = isSubRegion === 1
              ? regionsResult.find(r => r.idRegion === region.parentRegion)
              : null;

            // Node sayısını hesapla
            const nodeCount = nodesResult.filter(
              node => node.region_idRegion === region.idRegion
            ).length;

            if (isSubRegion === 1) {
              // Alt bölge
              allRegionsData.push({
                id: `sub_${region.idRegion}`,
                regionId: region.parentRegion,
                regionName: parentRegion?.name || t('regions.messages.unknownRegion', 'Bilinmeyen Bölge'),
                subRegionId: region.idRegion,
                subRegionName: region.name,
                isSubRegion: 1,
                subRegionTodaySale: 0,
                subRegionYesterdaySale: 0,
                totalNodeQuantity: nodeCount,
                onlineNodeQuantity: 0,
                offilineNodeQuantity: nodeCount,
                alarmedNodeQuantity: 0,
                warningNodeQuantity: 0
              });
            } else {
              // Ana bölge
              allRegionsData.push({
                id: `main_${region.idRegion}`,
                regionId: region.idRegion,
                regionName: region.name,
                isSubRegion: 0,
                regionTodaySale: 0,
                regionYesterdaySale: 0,
                totalNodeQuantity: nodeCount,
                onlineNodeQuantity: 0,
                offilineNodeQuantity: nodeCount,
                alarmedNodeQuantity: 0,
                warningNodeQuantity: 0
              });
            }
          }
        });

        setData(allRegionsData);
      } catch (err) {
        setError(err.message || t('regions.messages.errorLoadingData', 'Veri yüklenirken bir hata oluştu'));
        console.error('Error fetching region sale reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleRegionAdded = async () => {
    try {
      const [saleReportsResult, regionsResult, nodesResult] = await Promise.all([
        getRegionSaleReports(),
        getRegion(),
        getNode()
      ]);

      setRegions(regionsResult);

      // Satış verilerini map'e çevir
      const saleReportsMap = new Map();
      saleReportsResult.forEach(report => {
        const key = report.isSubRegion === 1
          ? `sub_${report.subRegionId}`
          : `main_${report.regionId}`;
        saleReportsMap.set(key, report);
      });

      // Tüm bölgeleri işle
      const allRegionsData = [];

      regionsResult.forEach(region => {
        // Ana bölge mi alt bölge mi kontrol et
        // Level hesapla: parentRegion yoksa Level 1, varsa parent'ın level'ı + 1
        const regionLevel = getRegionLevel(region, regionsResult);

        // Level 1'leri atla, sadece Level 2 ve Level 3'ü işle
        if (regionLevel === 1) {
          return;
        }

        // Level 2 = Ana bölge (isSubRegion: 0)
        // Level 3 = Alt bölge (isSubRegion: 1)
        const isSubRegion = regionLevel === 3 ? 1 : 0;

        const key = isSubRegion === 1
          ? `sub_${region.idRegion}`
          : `main_${region.idRegion}`;

        // Satış verisi var mı kontrol et
        const saleReport = saleReportsMap.get(key);

        if (saleReport) {
          // Satış verisi varsa mevcut veriyi kullan
          allRegionsData.push(saleReport);
        } else {
          // Satış verisi yoksa, temel veriyi ekle
          const parentRegion = isSubRegion === 1
            ? regionsResult.find(r => r.idRegion === region.parentRegion)
            : null;

          // Node sayısını hesapla
          const nodeCount = nodesResult.filter(
            node => node.region_idRegion === region.idRegion
          ).length;

          if (isSubRegion === 1) {
            // Alt bölge
            allRegionsData.push({
              id: `sub_${region.idRegion}`,
              regionId: region.parentRegion,
              regionName: parentRegion?.name || t('regions.messages.unknownRegion', 'Bilinmeyen Bölge'),
              subRegionId: region.idRegion,
              subRegionName: region.name,
              isSubRegion: 1,
              subRegionTodaySale: 0,
              subRegionYesterdaySale: 0,
              totalNodeQuantity: nodeCount,
              onlineNodeQuantity: 0,
              offilineNodeQuantity: nodeCount,
              alarmedNodeQuantity: 0,
              warningNodeQuantity: 0
            });
          } else {
            // Ana bölge
            allRegionsData.push({
              id: `main_${region.idRegion}`,
              regionId: region.idRegion,
              regionName: region.name,
              isSubRegion: 0,
              regionTodaySale: 0,
              regionYesterdaySale: 0,
              totalNodeQuantity: nodeCount,
              onlineNodeQuantity: 0,
              offilineNodeQuantity: nodeCount,
              alarmedNodeQuantity: 0,
              warningNodeQuantity: 0
            });
          }
        }
      });

      setData(allRegionsData);
    } catch (err) {
      console.error('Error refreshing data:', err);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleRowClick = async (params) => {
    const clickedRow = params.row;

    // Hiyerarşik görünümde tıklanan satıra göre regionId'yi belirle
    let regionId;
    if (viewMode === 'hierarchical') {
      // Parent (ana bölge) tıklandıysa
      if (clickedRow.isParent) {
        regionId = clickedRow.regionId;
      } else {
        // Child (alt bölge) tıklandıysa
        regionId = clickedRow.subRegionId;
      }
    } else if (viewMode === 'subregion') {
      regionId = clickedRow.subRegionId;
    } else {
      regionId = clickedRow.regionId;
    }

    setSelectedRegion(clickedRow);
    setNodesLoading(true);

    // Chart'ları güncellemek için parent component'e bildiriyoruz
    if (onRegionSelect) {
      if (viewMode === 'hierarchical' && clickedRow.isParent) {
        // Hiyerarşik görünümde parent row'a tıklanmışsa ana regionId'yi gönder
        onRegionSelect(clickedRow.regionId);
      } else if (viewMode === 'subregion') {
        // Subregion görünümünde, alt bölgenin parent regionId'sini gönder
        // clickedRow.regionId aslında parent region'ın ID'si
        onRegionSelect(clickedRow.regionId);
      } else if (viewMode === 'region') {
        // Region görünümünde direkt regionId'yi gönder
        onRegionSelect(regionId);
      }
    }

    try {
      const [allNodes, allNodesWithStatus, devicesData] = await Promise.all([
        getNode(),
        getNodeWithStatus(),
        getDevices(),
      ]);

      let filteredNodeIds;

      if (viewMode === 'hierarchical') {
        if (clickedRow.isParent) {
          // Ana bölgeye tıklandıysa, hem kendisi hem de alt bölgelerindeki node'ları göster
          const childRegions = regions.filter(region => region.parentRegion === regionId);
          const childRegionIds = childRegions.map(region => region.idRegion);
          const allRegionIds = [regionId, ...childRegionIds];
          const filteredNodes = allNodes.filter(node => allRegionIds.includes(node.region_idRegion));
          filteredNodeIds = filteredNodes.map(node => node.idNode);
        } else {
          // Alt bölgeye tıklandıysa, sadece o bölgedeki node'ları göster
          const filteredNodes = allNodes.filter(node => node.region_idRegion === regionId);
          filteredNodeIds = filteredNodes.map(node => node.idNode);
        }
      } else if (viewMode === 'subregion') {
        const filteredNodes = allNodes.filter(node => node.region_idRegion === regionId);
        filteredNodeIds = filteredNodes.map(node => node.idNode);
      } else {
        // Region görünümü - ana bölge ve alt bölgelerindeki tüm node'lar
        const childRegions = regions.filter(region => region.parentRegion === regionId);
        const childRegionIds = childRegions.map(region => region.idRegion);
        const allRegionIds = [regionId, ...childRegionIds];
        const filteredNodes = allNodes.filter(node => allRegionIds.includes(node.region_idRegion));
        filteredNodeIds = filteredNodes.map(node => node.idNode);
      }

      // NodesWithStatus'u filtrele
      const filteredNodesWithStatus = allNodesWithStatus.filter(node =>
        filteredNodeIds.includes(node.NID)
      );

      setNodes(allNodes);
      setNodesWithStatus(filteredNodesWithStatus);
      setDevices(devicesData.data || []);
    } catch (err) {
      console.error('Error fetching nodes:', err);
      setNodes([]);
      setNodesWithStatus([]);
      setDevices([]);
    } finally {
      setNodesLoading(false);
    }
  };

  const toggleRegionExpand = useCallback((regionId) => {
    setExpandedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  }, []);

  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    console.log('Menu açıldı, seçili satır:', row);
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    console.log('Düzenle:', selectedRow);
    setEditDialogOpen(true);
    setAnchorEl(null); // Close menu, keep selectedRow
  };

  const handleDelete = () => {
    console.log('handleDelete çağrıldı, selectedRow:', selectedRow);
    setDeleteDialogOpen(true);
    // Menüyü kapat ama selectedRow'u tutmak için ayrı fonksiyon kullan
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      console.log('handleDeleteConfirm çağrıldı, selectedRow:', selectedRow);

      // Silinecek bölgenin ID'sini belirle
      // Alt bölge görünümünde: subRegionId aslında silinecek alt bölgenin kendi ID'si
      // Ana bölge görünümünde: regionId kullanılır
      let regionId;

      if (viewMode === 'subregion') {
        // Alt bölge görünümünde subRegionId, silinecek bölgenin kendi idRegion'ı
        regionId = selectedRow.subRegionId;
      } else {
        // Ana bölge görünümünde
        regionId = selectedRow.regionId;
      }

      console.log('Silinecek bölge ID:', regionId, 'viewMode:', viewMode);

      await deleteRegion(regionId);

      console.log('Bölge başarıyla silindi');

      // Başarılı silme bildirimi
      showSnackbar(
        t('regions.messages.regionDeleted', 'Bölge başarıyla silindi'),
        'success'
      );

      // Verileri yeniden yükle - handleRegionAdded ile aynı mantık
      await handleRegionAdded();
    } catch (error) {
      console.error('Error deleting region:', error);
      showSnackbar(
        t('regions.messages.errorDeletingRegion', 'Bölge silinirken bir hata oluştu'),
        'error'
      );
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };


  const handleDownloadReport = () => {
    try {
      const result = exportRegionReport(data, regions);
      if (result.success) {
        showSnackbar(
          t('regions.messages.reportDownloaded', 'Bölge raporu başarıyla indirildi: {{fileName}}', { fileName: result.fileName }),
          'success'
        );
      } else {
        showSnackbar(
          t('regions.messages.errorCreatingReport', 'Rapor oluşturulurken bir hata oluştu'),
          'error'
        );
      }
    } catch (error) {
      console.error('Report download error:', error);
      showSnackbar(
        t('regions.messages.errorDownloadingReport', 'Rapor indirilemedi'),
        'error'
      );
    }
  };

  const hierarchicalData = useMemo(() => {
    if (viewMode !== 'hierarchical') {
      // Eski görünüm modları için
      if (viewMode === 'subregion') {
        return data.filter(item => item.isSubRegion === 1);
      } else {
        return data.filter(item => item.isSubRegion === 0);
      }
    }

    // Hiyerarşik görünüm için
    const result = [];
    const parentRegions = data.filter(item => item.isSubRegion === 0);

    parentRegions.forEach(parent => {
      // Ana bölgeyi ekle
      result.push({
        ...parent,
        isParent: true,
        level: 0,
        id: `region-${parent.regionId}`
      });

      // Eğer bölge genişletilmişse, alt bölgelerini ekle
      if (expandedRegions[parent.regionId]) {
        const children = data.filter(
          item => item.isSubRegion === 1 && item.regionId === parent.regionId
        );

        children.forEach(child => {
          result.push({
            ...child,
            isParent: false,
            level: 1,
            id: `subregion-${child.subRegionId}`
          });
        });
      }
    });

    return result;
  }, [data, viewMode, expandedRegions]);

  const filteredData = hierarchicalData;

  const detailRows = useMemo(
    () =>
      nodesWithStatus.map((nodeWithStatus) => {
        const nid = nodeWithStatus.NID;
        const nodeName = nodeWithStatus.NodeName;

        // NID ile getDevices listesinden device bilgisini bul (NID = node_idNode)
        const device = devices.find((d) => d.node_idNode === nid);
        const deviceName = device?.nameDevice || "-";

        // Alarm status kontrolü - nodeWithStatus verisindeki tüm alarmLevel değerlerini kontrol et
        let alarmStatus = -1; // Başlangıç değeri
        const alarmFields = ["20", "5003", "5004", "5005", "5006", "5007", "5008", "5009", "5010"];

        for (const field of alarmFields) {
          const fieldData = nodeWithStatus[field];
          if (fieldData && typeof fieldData === "object" && fieldData.alamLevel !== undefined) {
            const level = fieldData.alamLevel;
            // En yüksek alarm level'ı bul
            if (level === 2) {
              alarmStatus = 2; // Alarm bulundu, döngüden çık
              break;
            } else if (level === 1 && alarmStatus !== 2) {
              alarmStatus = 1; // Warning
            } else if (level === 0 && alarmStatus === -1) {
              alarmStatus = 0; // Normal (sadece henüz alarm veya warning bulunmadıysa)
            }
          }
        }

        // Basic ve Alarm field'larının değerlerini al ve convert et
        const fieldValues = {};
        const basicFields = ["2", "4", "6", "7", "18"];
        const salesFields = ["71", "72"]; // Sales today ve sales yesterday

        // Basic field'ları işle
        for (const fieldId of basicFields) {
          const rawValue = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (rawValue !== undefined && rawValue !== null) {
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString2(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
          } else {
            fieldValues[`field_${fieldId}`] = "-";
          }
        }

        // Sales field'ları işle (71, 72)
        for (const fieldId of salesFields) {
          const rawValue = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (rawValue !== undefined && rawValue !== null) {
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString2(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
          } else {
            fieldValues[`field_${fieldId}`] = "-";
          }
        }

        // Alarm field'ları işle
        for (const fieldId of alarmFields) {
          const fieldData = nodeWithStatus[fieldId];
          const objectInfo = objectData.find((obj) => obj.idObject === parseInt(fieldId));

          if (fieldData && typeof fieldData === "object" && fieldData.val !== undefined) {
            const rawValue = fieldData.val;
            // type'a göre değeri convert et
            if (objectInfo && objectInfo.type !== undefined) {
              const convertedValue = ConvertOIDValueToString2(
                { Type: objectInfo.type },
                rawValue
              );
              fieldValues[`field_${fieldId}`] = convertedValue;
            } else {
              fieldValues[`field_${fieldId}`] = rawValue;
            }
            // Alarm level'ı da sakla
            fieldValues[`field_${fieldId}_alarmLevel`] = fieldData.alamLevel;
          } else {
            fieldValues[`field_${fieldId}`] = "-";
            fieldValues[`field_${fieldId}_alarmLevel`] = undefined;
          }
        }

        return {
          id: nid,
          nid: nid,
          nodeName: nodeName,
          terminal: deviceName,
          alarmStatus: alarmStatus,
          ...fieldValues, // Dinamik field değerlerini ekle
        };
      }),
    [nodesWithStatus, devices, objectData]
  );

  const columns = useMemo(() => {
    const commonColumns = [
      {
        field: 'totalNodeQuantity',
        headerName: t('regions.dataGrid.totalNodeQuantity'),
        flex: 1,
        type: 'number',
        align: 'center',
        headerAlign: 'center'
      },
      {
        field: 'onlineNodeQuantity',
        headerName: t('regions.dataGrid.onlineNodeQuantity'),
        flex: 1,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value}
            color="success"
            size="small"
            variant="outlined"
          />
        )
      },
      {
        field: 'offilineNodeQuantity',
        headerName: t('regions.dataGrid.offilineNodeQuantity'),
        flex: 1,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value}
            color="default"
            size="small"
            variant="outlined"
          />
        )
      },
      {
        field: 'alarmedNodeQuantity',
        headerName: t('regions.dataGrid.alarmedNodeQuantity'),
        flex: 1,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value}
            color="error"
            size="small"
            variant="outlined"
          />
        )
      },
      {
        field: 'warningNodeQuantity',
        headerName: t('regions.dataGrid.warningNodeQuantity'),
        flex: 1,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value}
            color="warning"
            size="small"
            variant="outlined"
          />
        )
      }
    ];

    if (viewMode === 'hierarchical') {
      // Hiyerarşik görünüm
      return [
        {
          field: 'regionName',
          headerName: t('regions.dataGrid.regionName'),
          flex: 1,
          align: 'left',
          headerAlign: 'center',
          renderCell: (params) => {
            const isParent = params.row.isParent;

            // Alt bölge satırında boş
            if (!isParent) return null;

            // Ana bölge satırında bölge ismi ve dropdown göster
            const hasChildren = data.some(
              item => item.isSubRegion === 1 && item.regionId === params.row.regionId
            );

            return (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                pl: 2
              }}>
                {hasChildren ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRegionExpand(params.row.regionId);
                    }}
                    sx={{
                      padding: '4px',
                      mr: 1,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.2rem'
                      }
                    }}
                  >
                    {expandedRegions[params.row.regionId] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 36, mr: 1 }} />
                )}
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    fontSize: '0.95rem',
                    lineHeight: 1,
                  }}
                >
                  {params.row.regionName}
                </Typography>
              </Box>
            );
          }
        },
        {
          field: 'subRegionName',
          headerName: t('regions.dataGrid.subRegionName'),
          flex: 1,
          align: 'left',
          headerAlign: 'center',
          renderCell: (params) => {
            const isParent = params.row.isParent;

            // Ana bölge satırında - dropdown açıksa ters L göster
            if (isParent) {
              const isExpanded = expandedRegions[params.row.regionId];

              if (!isExpanded) {
                return null; // Dropdown kapalıysa hiçbir şey gösterme
              }

              return (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  pl: 2
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#bdbdbd'
                  }}>
                    <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>─┐</Typography>
                  </Box>
                </Box>
              );
            }

            // Alt bölge satırında alt bölge ismini göster
            return (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                pl: 2
              }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    lineHeight: 1,
                  }}
                >
                  {params.row.subRegionName}
                </Typography>
              </Box>
            );
          }
        },
        {
          field: 'todaySale',
          headerName: t('regions.dataGrid.salesToday', 'Satış (Bugün)'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            const saleValue = row.isParent ? row.regionTodaySale : row.subRegionTodaySale;
            if (saleValue == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, saleValue);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        {
          field: 'yesterdaySale',
          headerName: t('regions.dataGrid.salesYesterday', 'Satış (Dün)'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            const saleValue = row.isParent ? row.regionYesterdaySale : row.subRegionYesterdaySale;
            if (saleValue == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, saleValue);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        ...commonColumns.map(col => ({
          ...col,
          renderCell: (params) => {
            // Ana bölge satırında değerleri göster
            if (params.row.isParent) {
              if (col.renderCell) {
                return col.renderCell(params);
              }
              return params.value;
            }
            // Alt bölge satırında değerleri göster
            return col.renderCell ? col.renderCell(params) : params.value;
          }
        }))
      ];
    } else if (viewMode === 'subregion') {
      return [
        {
          field: 'actions',
          headerName: '',
          width: 70,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            return (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClick(event, params.row);
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            );
          },
        },
        {
          field: 'regionName',
          headerName: t('regions.dataGrid.regionName'),
          flex: 1,
          align: 'center',
          headerAlign: 'center'
        },
        {
          field: 'subRegionName',
          headerName: t('regions.dataGrid.subRegionName'),
          flex: 1,
          align: 'center',
          headerAlign: 'center'
        },

        {
          field: 'subRegionTodaySale',
          headerName: t('regions.dataGrid.subRegionTodaySale'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            if (row?.subRegionTodaySale == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, row.subRegionTodaySale);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        {
          field: 'subRegionYesterdaySale',
          headerName: t('regions.dataGrid.subRegionYesterdaySale'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            if (row?.subRegionYesterdaySale == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, row.subRegionYesterdaySale);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        ...commonColumns
      ];
    } else {
      return [
        {
          field: 'actions',
          headerName: '',
          width: 70,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            return (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClick(event, params.row);
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            );
          },
        },
        {
          field: 'regionName',
          headerName: t('regions.dataGrid.regionName'),
          flex: 1,
          align: 'center',
          headerAlign: 'center'
        },

        {
          field: 'regionTodaySale',
          headerName: t('regions.dataGrid.regionTodaySale'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            if (row?.regionTodaySale == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, row.regionTodaySale);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        {
          field: 'regionYesterdaySale',
          headerName: t('regions.dataGrid.regionYesterdaySale'),
          flex: 1,
          type: 'number',
          align: 'center',
          headerAlign: 'center',
          valueGetter: (_value, row) => {
            if (row?.regionYesterdaySale == null) return null;
            const converted = ConvertOIDValueToString({ type: 11 }, row.regionYesterdaySale);
            return parseFloat(converted);
          },
          valueFormatter: (value) => {
            if (value == null) return '';
            return value.toLocaleString('tr-TR') + ' TL';
          }
        },
        ...commonColumns
      ];
    }
  }, [viewMode, t, data, expandedRegions, toggleRegionExpand]);

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flexGrow: 1,
        }}
      >
        <CardContent>
          <Box sx={{
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { md: 'space-between' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2
          }}>
            {/* Butonlar - Mobilde üstte */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              order: { xs: 1, md: 1 }
            }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size="medium"
                fullWidth={{ xs: true, sm: false }}
                sx={{ borderRadius: 2 }}
                onClick={handleOpenDialog}
              >
                {t('regions.controls.addRegion')}
              </Button>

              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                size="medium"
                fullWidth={{ xs: true, sm: false }}
                sx={{ borderRadius: 2, color: 'white' }}
                onClick={handleDownloadReport}
              >
                {t('regions.controls.downloadReport', 'Bölge Raporu İndir')}
              </Button>
            </Box>

            {/* Görünüm Toggle - Mobilde altta */}
            <Box sx={{
              display: 'flex',
              justifyContent: { xs: 'stretch', md: 'flex-end' },
              order: { xs: 2, md: 2 }
            }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                color="primary"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  '& .MuiToggleButton-root': {
                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                    py: { xs: 1, sm: 'auto' }
                  }
                }}
              >
                <ToggleButton value="hierarchical" aria-label="show hierarchical">
                  {t('regions.dataGrid.hierarchicalView', 'Hiyerarşik Görünüm')}
                </ToggleButton>
                <ToggleButton value="subregion" aria-label="show subregions">
                  {t('regions.dataGrid.showSubRegions')}
                </ToggleButton>
                <ToggleButton value="region" aria-label="show regions">
                  {t('regions.dataGrid.showRegions')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box sx={{ height: { xs: 500, sm: 400 }, width: '100%' }}>
            <DataGrid
              rows={filteredData}
              columns={columns}
              getRowId={(row) => row.id || `${row.regionId}-${row.subRegionId}`}
              loading={loading}
              onRowClick={handleRowClick}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 }
                }
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 0.5, sm: 1.5 },
                  py: { xs: 1, sm: 'auto' },
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-columnHeader': {
                  px: { xs: 0.5, sm: 1.5 }
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {selectedRegion ? (
        <Paper elevation={3} sx={{ mt: 1, p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {viewMode === 'hierarchical'
              ? (selectedRegion.isParent
                ? selectedRegion.regionName
                : `${selectedRegion.regionName} - ${selectedRegion.subRegionName}`)
              : viewMode === 'subregion'
              ? `${selectedRegion.regionName} - ${selectedRegion.subRegionName}`
              : selectedRegion.regionName} {t('regions.dataGrid.automats')}
          </Typography>
          <Box sx={{ height: { xs: 400, sm: 450 }, width: '100%', position: 'relative' }}>
            {nodesLoading && detailRows.length === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              rows={detailRows}
              columns={getDetailColumns(t, objectData)}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 15,
                  },
                },
              }}
              pageSizeOptions={[15, 20, 50]}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                const nid = params.row.nid;
                window.open(`/vending-machines/${nid}`, "_blank");
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  px: { xs: 0.5, sm: 1 },
                  py: { xs: 1, sm: 'auto' },
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  minHeight: { xs: '48px !important', sm: 'auto' }
                },
                '& .MuiDataGrid-columnHeader': {
                  px: { xs: 0.5, sm: 1 }
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto'
                }
              }}
            />
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={3}
          sx={{
            mt: 1,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              backgroundColor: "primary.light",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  opacity: 0.6,
                  transform: "scale(1)",
                },
                "50%": {
                  opacity: 1,
                  transform: "scale(1.05)",
                },
              },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: "primary.contrastText",
                  fontWeight: 300,
                }}
              >
                ↑
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="h6"
            sx={{
              mt: 3,
              color: 'text.secondary',
              textAlign: 'center'
            }}
          >
            {t('regions.dataGrid.selectRegionToViewDetails')}
          </Typography>
        </Paper>
      )}

      <AddRegionDialog
        open={openDialog}
        onClose={handleCloseDialog}
        regions={regions}
        onRegionAdded={handleRegionAdded}
      />

      <EditRegionDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        regions={regions}
        onRegionUpdated={handleRegionAdded}
        regionToEdit={selectedRow}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          {t('common.edit', 'Düzenle')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          {t('common.delete', 'Sil')}
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('regions.dialogs.deleteRegion.title', 'Bölgeyi Sil')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('regions.dialogs.deleteRegion.confirmMessage', 'Bu bölgeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('common.cancel', 'İptal')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            {t('common.delete', 'Sil')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

RegionGrid.propTypes = {
  onRegionSelect: PropTypes.func,
};

export default RegionGrid;
